import { CustomerMailer } from '@/modules/Customer/types'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Mail, Tag, Pencil } from 'lucide-react'
import { cn } from '@/lib/utils'

interface MailerCardProps {
    mailer: CustomerMailer
    compact?: boolean
    onEdit: (mailer: CustomerMailer) => void
    onEmailClick?: (email: string) => void
    selectedEmail?: string | null
}

const MailerCard: React.FC<MailerCardProps> = ({
    mailer,
    compact = false,
    onEdit,
    onEmailClick,
    selectedEmail,
}) => {
    const { t } = useTranslation('customer')
    const emails =
        mailer?.mailer_receiver?.split(',').map((e) => e.trim()) || []

    return (
        <div className="group border rounded-lg p-3 hover:border-primary/50 hover:shadow-sm transition-all bg-card">
            {/* Header */}
            <div className="flex items-start gap-2 mb-2">
                <div
                    className={cn(
                        'w-1.5 h-1.5 rounded-full mt-2 shrink-0',
                        mailer?.sendable ? 'bg-green-500' : 'bg-red-400'
                    )}
                />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <h4 className="font-medium text-sm truncate">
                            {mailer?.customer_mailer_name}
                        </h4>
                        <Badge
                            variant={mailer?.sendable ? 'default' : 'secondary'}
                            className="h-5 text-[10px] shrink-0"
                        >
                            {mailer?.sendable
                                ? t('mailer.status.active')
                                : t('mailer.status.inactive')}
                        </Badge>
                    </div>

                    {/* Stats */}
                    <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {emails.length}
                        </span>
                        {mailer?.mailer_types &&
                            mailer.mailer_types.length > 0 && (
                                <span className="flex items-center gap-1">
                                    <Tag className="w-3 h-3" />
                                    {mailer.mailer_types.length}
                                </span>
                            )}
                    </div>
                </div>

                <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                    onClick={(e) => {
                        e.stopPropagation()
                        onEdit(mailer)
                    }}
                >
                    <Pencil className="w-3 h-3" />
                </Button>
            </div>

            {/* Types */}
            {mailer?.mailer_types &&
                mailer.mailer_types.length > 0 &&
                !compact && (
                    <div className="flex flex-wrap gap-1 mb-2">
                        {mailer.mailer_types.slice(0, 2).map((type, i) => (
                            <Badge
                                key={i}
                                variant="secondary"
                                className="text-[10px] h-5 font-normal"
                            >
                                {type?.mailer_description}
                            </Badge>
                        ))}
                        {mailer.mailer_types.length > 2 && (
                            <Badge
                                variant="outline"
                                className="text-[10px] h-5"
                            >
                                +{mailer.mailer_types.length - 2}
                            </Badge>
                        )}
                    </div>
                )}

            {/* Emails */}
            {emails.length > 0 && !compact && (
                <div className="flex flex-wrap gap-1 pt-2 border-t">
                    {emails.slice(0, 2).map((email, i) => (
                        <Badge
                            key={i}
                            variant={
                                selectedEmail === email ? 'default' : 'outline'
                            }
                            className={cn(
                                'text-[10px] h-5 cursor-pointer transition-colors',
                                selectedEmail === email ? '' : 'hover:bg-accent'
                            )}
                            onClick={() => onEmailClick?.(email)}
                        >
                            {email}
                        </Badge>
                    ))}
                    {emails.length > 2 && (
                        <Badge variant="outline" className="text-[10px] h-5">
                            +{emails.length - 2}
                        </Badge>
                    )}
                </div>
            )}
        </div>
    )
}

export default MailerCard
